import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { EVAL_PAGE_LDAP_GROUP } from '../../config';
import { useImpersonation } from '../../context/ImpersonationContext';
import { useWorkspaceNav } from '../../context/WorkspaceNavContext';
import { useChatPanel } from '../../context/ChatPanelContext';
import UserPopover from './UserPopover';
import './Sidebar.css';


const BADGE_PHOTO_URL = 'https://badgephotos.corp.amazon.com/';

/** Nav labels in the `shell` namespace (`nav.*`). Kept as keys so they follow the UI locale. */
type NavLabelKey = 'workspace' | 'skills' | 'tasks' | 'eval' | 'feedback';

export interface NavItem {
  id: string;
  icon: string;
  activeIcon: string;
  labelKey: NavLabelKey;
  href: string;
  disabled?: boolean;
  iconSize?: number;
}

const NAV_ITEMS_TOP: NavItem[] = [
  { id: 'report', icon: '/icons/icon-workspace.svg', activeIcon: '/icons/icon-workspace-active.svg', labelKey: 'workspace', href: '/', iconSize: 32 },
  { id: 'skills', icon: '/icons/icon-apps.svg', activeIcon: '/icons/icon-apps-active.svg', labelKey: 'skills', href: '/skills' },
  { id: 'history', icon: '/icons/icon-task.svg', activeIcon: '/icons/icon-task-active.svg', labelKey: 'tasks', href: '/tasks' },
];

/** LDAP group required to access the Evaluation module (mirrors EvalLayout). */

const EVAL_NAV_ITEM: NavItem = {
  id: 'eval',
  icon: '/icons/icon-eval.png',
  activeIcon: '/icons/icon-eval.png',
  labelKey: 'eval',
  href: '/eval/landing',
};

const NAV_ITEMS_BOTTOM: NavItem[] = [
  { id: 'mail', icon: '/icons/icon-mail.svg', activeIcon: '/icons/icon-mail-active.svg', labelKey: 'feedback', href: 'https://t.corp.amazon.com/create/templates/890869c5-89ed-4047-aa91-105ff10dff2d' },
];

interface SidebarProps {
  /**
   * Main app only. On phones (≤767px, Sidebar.css) the rail becomes an off-canvas
   * drawer that MobileTopBar opens; any nav click closes it again. Event Hub renders
   * <Sidebar /> without this and keeps the fixed rail at every width — leave it so.
   */
  drawer?: { open: boolean; onClose: () => void };
}

export default function Sidebar({ drawer }: SidebarProps = {}) {
  const { t } = useTranslation('shell');
  const navigate = useNavigate();
  const location = useLocation();
  const { uid, display_name, ldap_groups } = useAuth();
  const { isImpersonating, targetAlias } = useImpersonation();
  const { toggleNav, navCollapsed, setSelectedFile } = useWorkspaceNav();
  const { exitFullscreen, chatFullscreen } = useChatPanel();
  const [popoverOpen, setPopoverOpen] = useState(false);

  // The user menu lives inside the drawer on phones; closing the drawer (backdrop,
  // ✕, navigation) must not leave it floating over the page.
  const drawerOpen = drawer?.open;
  useEffect(() => {
    if (!drawerOpen) setPopoverOpen(false);
  }, [drawerOpen]);

  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  const handleNav = (href: string) => {
    exitFullscreen();
    navigate(href);
  };

  const renderNavItem = (item: NavItem) => {
    const active = isActive(item.href);
    const label = t(`nav.${item.labelKey}`);
    const showArrow = item.id === 'report' && active && navCollapsed;
    return (
      <li key={item.id}>
        <button
          className={`sidebar__nav-item${active ? ' sidebar__nav-item--active' : ''}${item.disabled ? ' sidebar__nav-item--disabled' : ''}`}
          onClick={() => {
            if (item.disabled) return;
            // Including the Workspace re-click that toggles the file tree: the drawer
            // would otherwise sit on top of the tree the user just asked to see.
            drawer?.onClose();
            if (item.href.startsWith('http')) {
              window.open(item.href, '_blank', 'noopener,noreferrer');
              return;
            }
            if (item.id === 'report' && active && chatFullscreen) {
              exitFullscreen();
            } else if (item.id === 'report' && active) {
              toggleNav();
            } else {
              handleNav(item.href);
            }
          }}
          title={item.disabled ? t('nav.comingSoon', { label }) : label}
          aria-label={label}
          aria-current={active ? 'page' : undefined}
          aria-disabled={item.disabled || undefined}
        >
          <img
            src={active ? item.activeIcon : item.icon}
            alt=""
            style={item.iconSize ? { width: item.iconSize, height: item.iconSize } : undefined}
          />
          {showArrow && (
            <img className="sidebar__nav-arrow" src="/icons/angle-left.svg" alt="" />
          )}
          <span className="sidebar__nav-label">{label}</span>
        </button>
      </li>
    );
  };

  // Show target user's avatar when impersonating
  const avatarUid = isImpersonating ? targetAlias : uid;

  const goHome = () => {
    drawer?.onClose();
    setSelectedFile(null);
    handleNav('/');
  };

  const navClass = `sidebar${drawer ? ' sidebar--drawer' : ''}${drawer?.open ? ' sidebar--open' : ''}`;

  return (
    <nav className={navClass} aria-label={t('nav.label')}>
      {/* Portalled out of <nav>: the drawer slides with `transform`, which would make
          a fixed-position child cover the nav instead of the viewport. */}
      {drawer?.open && createPortal(
        <div className="sidebar__backdrop" onClick={drawer.onClose} aria-hidden="true" />,
        document.body,
      )}
      {/* Phones only (Sidebar.css): MainAppContent passes `drawer` at every width,
          so the button is rendered there but hidden outside the phone drawer. It is
          absolutely positioned, and sits BEFORE the logo on purpose: Sidebar.css
          spaces the nav with `.sidebar__logo + .sidebar__nav`, which any element
          between the two — even a display:none one — would break. */}
      {drawer && (
        <button className="sidebar__drawer-close" onClick={drawer.onClose} aria-label={t('nav.closeMenu')}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path d="M5 5L15 15M15 5L5 15" stroke="#424650" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      )}
      {/* Logo */}
      <div
        className="sidebar__logo"
        onClick={goHome}
        role="button"
        tabIndex={0}
        aria-label={t('nav.home')}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            goHome();
          }
        }}
      >
        <img src="/icons/avatar-rocky.png" alt={t('nav.logoAlt')} />
      </div>

      {/* Top nav items: Home, Reports, Apps, Files */}
      <ul className="sidebar__nav">
        {NAV_ITEMS_TOP.map(renderNavItem)}
        {ldap_groups.includes(EVAL_PAGE_LDAP_GROUP) && renderNavItem(EVAL_NAV_ITEM)}
      </ul>

      {/* Divider — between Files and Mail per Figma */}
      <div className="sidebar__divider" role="separator" />

      {/* Bottom nav items: Mail */}
      <ul className="sidebar__nav">
        {NAV_ITEMS_BOTTOM.map(renderNavItem)}
      </ul>

      {/* Spacer */}
      <div className="sidebar__spacer" />

      {/* User avatar — clickable for everyone; the popover gates its
          admin-only sections (View as / Backend) internally. */}
      <div
        className={`sidebar__user ${isImpersonating ? 'sidebar__user--impersonating' : ''} sidebar__user--clickable`}
        title={isImpersonating ? t('user.viewingAs', { alias: targetAlias }) : (display_name || t('user.fallbackName'))}
        onClick={() => setPopoverOpen(!popoverOpen)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setPopoverOpen(!popoverOpen);
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={popoverOpen}
        aria-haspopup="dialog"
      >
        <div className="sidebar__user-avatar">
          <img
            src={`${BADGE_PHOTO_URL}?uid=${avatarUid}`}
            alt={avatarUid || t('user.fallbackName')}
            onError={(e) => {
              const img = e.target as HTMLImageElement;
              img.onerror = null;
              img.src = '/icons/user-avatar.svg';
            }}
          />
        </div>
        {/* Shown only in the phone drawer, where the row has room for it. */}
        <span className="sidebar__user-name">{avatarUid === uid ? (display_name || uid) : avatarUid}</span>
      </div>

      {/* User Popover — available to all users; admin-only sections
          (View as / Backend Management) are gated inside the popover. */}
      <UserPopover open={popoverOpen} onClose={() => setPopoverOpen(false)} />
    </nav>
  );
}
